import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import '../styles/ProjectsSidebar.css';
import API_BASE from '../utils/api.js';

function normalizeProject(raw) {
  return {
    id: raw.projectId || raw.id || 'untitled',
    name: raw.projectNickname || raw.name || raw.title || 'Untitled',
    createdAt: raw.createdAt || raw.created_at || raw.created || null,
    updatedAt: raw.updatedAt || raw.updated_at || raw.updated || null,
    isActive: raw.isActive ?? true,
  };
}

export default function ProjectsSidebar({ isOpen, onClose }) {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [activeOnly, setActiveOnly] = useState(true);

  async function loadProjects() {
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const apiBase = API_BASE;
      const res = await fetch(`${apiBase}/api/projects/${encodeURIComponent(userId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const normalized = (Array.isArray(data) ? data : [data])
        .map(normalizeProject)
        .sort(
          (a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
        );

      setProjects(normalized);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isOpen) return;

    loadProjects();
  }, [isOpen]);

  const filteredProjects = projects.filter((p) => {
    const matchesSearch = (p.name || '').toLowerCase().includes(search.toLowerCase());

    const isActive = p.isActive ?? true;

    return matchesSearch && (!activeOnly || isActive);
  });

  const selectedId = (() => {
    const parts = location.pathname.split('/');
    return parts[1] === 'projects' && parts[2] ? parts[2] : null;
  })();

  return (
    <div className={`projects-slideout ${isOpen ? 'open' : ''}`} aria-hidden={!isOpen}>
      <div className="projects-header">
        <h3>Projects</h3>
        <button className="close-btn" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="projects-search-container">
        <div className="projects-search-wrapper">
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <MagnifyingGlassIcon className="projects-search-icon" />
        </div>
      </div>

      <div className="projects-filter-row">
        <button
          className={`filter-btn ${activeOnly ? 'active' : ''}`}
          onClick={() => {
            setActiveOnly((v) => !v);
            loadProjects(); // 🔥 rehydrate
          }}
          type="button"
        >
          {activeOnly ? 'Showing Active Only' : 'All Projects'}
        </button>
      </div>

      <div className="projects-list-wrap">
        {loading && <div className="projects-loading">Loading…</div>}
        {error && <div className="projects-error">Error: {error}</div>}

        <ul className="projects-list">
          {filteredProjects.length === 0 && !loading && !error && (
            <li className="no-projects">No projects found.</li>
          )}
          {filteredProjects.map((project) => (
            <li
              key={project.id}
              className={`project-item ${selectedId === project.id ? 'selected' : ''}`}
            >
              <Link
                to={`/projects/${encodeURIComponent(project.id)}`}
                className="project-link"
                onClick={() => onClose()}
              >
                {project.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <Link to="/new-project" className="add-new">
        <span className="plus">＋</span>
        <span className="add-text">Add New</span>
      </Link>
    </div>
  );
}
